import React, { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Table, Input, Row, Col, Tag, message, Button, Space } from 'antd';
import { debounce } from 'lodash';
import { DoubleLine } from "../../../Components/Labels";
import { portfolioTypeString } from "../../../utils/strings";
import {KeyboardDoubleArrowRightRounded} from "@mui/icons-material";


const SelectedItem = ({ portfolio, onRemove }) => {
  return (
    <Tag
      style={{
        padding: '5px 10px',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#f0f2f5', // Soft background color for modern look
        border: 'none', // Removing the border for a modern tag look
      }}
      closable
      onClose={(e) => {
        e.preventDefault();
        onRemove(portfolio.id);
      }}
    >
      <Space>
         <span>{portfolio?.name ?? ''}</span>
      </Space>
    </Tag>
  );
};


const PortfolioSelector = ({ onSelect, initialSelectedPortfolios = [], allowTypes = ['b', 's', 'm'],
                             maxSelected = 20, width=500 }) => {
  const portfolios = useSelector(state => state.portfolios.items);
  const [selectedRowKeys, setSelectedRowKeys] = useState(initialSelectedPortfolios.map(p => p.id));
  const [selectedPortfolios, setSelectedPortfolios] = useState(initialSelectedPortfolios);
  const [searchValue, setSearchValue] = useState("");

  const filteredPortfolios = useMemo(() => {
    if (!searchValue || searchValue.trim() === "") {
      return portfolios.filter(portfolio => allowTypes.includes(portfolio.type));
    }
    return portfolios.filter(portfolio =>
      allowTypes.includes(portfolio.type) &&
      portfolio?.name?.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [portfolios, allowTypes, searchValue]);

  const onSelectChange = useCallback((newRowKeys, newRows) => {
    if (newRowKeys.length > maxSelected) {
      message.warning(`You can only select up to ${maxSelected} portfolios.`);
      return;
    }
    setSelectedRowKeys(newRowKeys);
    setSelectedPortfolios(newRows);
  }, [maxSelected]);

  const handleSearch = useCallback(debounce((value) => {
    setSearchValue(value || "");
  }, 300), []);

  const handleSubmit = useCallback(() => {
    onSelect(selectedPortfolios);
  }, [onSelect, selectedPortfolios]);

  const handleTagClose = useCallback((portfolioId) => {
    const newSelectedPortfolios = selectedPortfolios.filter(p => p.id !== portfolioId);
    setSelectedPortfolios(newSelectedPortfolios);
    setSelectedRowKeys(newSelectedPortfolios.map(p => p.id));
    onSelect(newSelectedPortfolios);
  }, [selectedPortfolios, onSelect]);
  const columns = [
    {
      title: 'Portfolio',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <DoubleLine
          title={record.name}
          text={portfolioTypeString(record.type)}
        />
      )
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <div>
      <Input.Search
        placeholder="Search Portfolios by Name"
        onChange={(e) => handleSearch(e.target.value)}
        size={'large'}
        enterButton={
          <Button
            className={'mx-2'}
            type={'primary'}
            variant={'contained'}
            sx={{ color: "#FFF" }}
            onClick={handleSubmit}
            disabled={selectedPortfolios.length===0}
            endIcon={<KeyboardDoubleArrowRightRounded />}
          >
            Continue
          </Button>
        }
      />
      {selectedPortfolios.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            {selectedPortfolios.map(portfolio => (
              <Col key={portfolio.id}>
                <SelectedItem portfolio={portfolio} onRemove={handleTagClose} />
              </Col>
            ))}
          </Row>
        </div>
      )}
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={filteredPortfolios}
        rowKey="id"
        pagination={false}
        scroll={{ y: 480 }}
      />
    </div>
  );
};

export default PortfolioSelector;
